import { addElement, deleteElements, FRPAction, FRPContext, FRPElement, FRPElementType, FRPLocalUserSettings, FRPLocalUserSettingsKey, FRPMessage, FRPNotificationContext, setView, setSelectedElementIds, setShownElementsIds, viewedElement } from "./FRPState";
import { Localization, SessionContext } from "@viamap/viamap2-common";
import { Fragment, useContext, useEffect, useState } from "react";
import { SaveAdd, Sort, TickCircle, Trash } from "iconsax-react";
import { FRPFunc } from "./FRPFunc";
import { useMitStorage } from "src/managers/UseMitStorage";
import { FloatingPortal, useDismiss, useFloating, useInteractions } from "@floating-ui/react";
import { GlassButton } from "src/components/GlassButtons";
import { MapitUtils } from "src/managers/MapitUtils";

enum ContentViews {
   Messages = "Messages",
   Elements = "Elements",
   Actions = "Actions",
}

function formatDate(date: Date) {
   const locale = Localization.getLocale();
   return date.toLocaleString(locale, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
   });
};

export function FRPContentView(props: {
   elementID: string | undefined,
   hoverElement: (elementID: string | undefined) => void
   exitElement: () => void
}) {

   const [view, setView] = useState<ContentViews>(ContentViews.Messages);
   const [localSettings, setLocalSettings] = useMitStorage<FRPLocalUserSettings>(FRPLocalUserSettingsKey, {} as FRPLocalUserSettings);


   return (
      <>
         <SelectContentView
            view={view} currentSort={localSettings[`${view}-sort`] || { dir: "asc", key: "date" }}
            elementID={props.elementID}
            onSort={(dir, key) => setLocalSettings((prev) => ({ ...prev, [`${view}-sort`]: { dir, key } }))}
            onSelect={(view) => setView(view)} />
         {view === ContentViews.Messages ?
            <MessageList
               sort={localSettings[`${view}-sort`] || { dir: "asc", key: "updated|created" }}
               elementID={props.elementID}
               hoverElement={props.hoverElement}
               exitElement={props.exitElement}
            />
            : null}
         {view === ContentViews.Actions ?
            <ActionList
               sort={localSettings[`${view}-sort`] || { dir: "asc", key: "date" }}
               elementID={props.elementID}
               hoverElement={props.hoverElement}
               exitElement={props.exitElement}
            />
            : null}
         {view === ContentViews.Elements ?
            <FRPElementList
               sort={localSettings[`${view}-sort`] || { dir: "asc", key: "lastChangedTimestamp" }}
               elementID={props.elementID}
               hoverElement={props.hoverElement}
               exitElement={props.exitElement}
            />
            : null}
      </>
   );
}

function SelectContentView(props: { view: ContentViews, elementID: string | undefined, selectedElementIds?: string[], currentSort: { dir: "asc" | "desc", key: string }, onSort?: (dir: "asc" | "desc", key: string) => void, onSelect?: (view: ContentViews) => void }) {

   const { state: frpState } = useContext(FRPContext);

   const contentFilter = FRPFunc.frpHandleFilterContent(frpState.filter, Object.values(frpState.elements), frpState);

   const views = [
      { type: ContentViews.Messages, label: Localization.getText("Messages"), count: () => frpState.messages.filter((msg) => msg.elementId === props.elementID || props.elementID === undefined).filter(contentFilter).length },
      {
         type: ContentViews.Elements, label: Localization.getText("Elements"), count: () => {
            if (props.selectedElementIds && props.selectedElementIds.length > 1) {
               return props.selectedElementIds.length;
            }
            return Object.values(frpState.elements).filter((el) => FRPFunc.filterByChildren(el, props.elementID, frpState.childrenLookupTable)).length;
         }
      },
      { type: ContentViews.Actions, label: Localization.getText("Actions"), count: () => frpState.actions.filter((action) => action.elementId === props.elementID || props.elementID === undefined).filter(contentFilter).length }
   ];


   return (
      <>
         <div className="FRP_ContentViewSelector ">
            {views.map((view) => {
               let count = view.count();
               return (
                  <div key={view.type} className={props.view === view.type ? "active" : ""} onClick={() => props.onSelect?.(view.type)}>
                     <span className="FRP_ContentViewLabel">{view.label}</span>
                     <span className="FRP_ContentViewCount">{count > 99 ? "99+" : count}</span>
                  </div>
               )
            })}
            <FrpSelectSort view={props.view} value={props.currentSort} onSort={props.onSort} />
         </div>

      </>
   );
}

function FrpSelectSort(props: { view: ContentViews, value: { dir: "asc" | "desc", key: string }, onSort?: (dir: "asc" | "desc", key: string) => void }) {
   const [open, setOpen] = useState(false);
   const { refs, floatingStyles, context } = useFloating({
      placement: "bottom-end",

      open: open,
      onOpenChange: setOpen,
   });
   const dismiss = useDismiss(context, { referencePress: false, outsidePress: true });
   const { getReferenceProps, getFloatingProps } = useInteractions([dismiss]);

   const sortOptions: { view: ContentViews, key: string, label: string, dir: "asc" | "desc" }[] = [
      { view: ContentViews.Elements, key: "lastChangedTimestamp", label: "Ældste først", dir: "asc" },
      { view: ContentViews.Elements, key: "lastChangedTimestamp", label: "Nyeste først", dir: "desc" },
      //{ view: ContentViews.Elements, key: "name", label: "Alfabetisk", dir: "desc" },
      { view: ContentViews.Elements, key: "id", label: "ID", dir: "desc" },
      { view: ContentViews.Messages, key: "updated|created", label: "Ældste først", dir: "asc" },
      { view: ContentViews.Messages, key: "updated|created", label: "Nyeste først", dir: "desc" },
      { view: ContentViews.Actions, key: "date", label: "Ældste først", dir: "asc" },
      { view: ContentViews.Actions, key: "date", label: "Nyeste først", dir: "desc" },
   ];


   return (
      <>
         <div ref={refs.setReference} className={"FRP_SortOptionsBTN " + (open ? "active" : "")} {...getReferenceProps()}>
            <Sort size={16} onClick={() => setOpen((prev) => !prev)} className="FRP_SortIcon" />
         </div>
         {
            open ?
               <FloatingPortal>
                  <div className="FRP_SortOptions" ref={refs.setFloating} style={floatingStyles} {...getFloatingProps()}>
                     {sortOptions.filter((option) => option.view === props.view).map((option) => (
                        <div key={option.key + option.dir} className={(props.value.key === option.key && props.value.dir === option.dir) ? "active" : ""} onClick={() => {
                           props.onSort?.(option.dir, option.key);
                        }}>
                           <TickCircle opacity={(props.value.key === option.key && props.value.dir === option.dir) ? 1 : 0} size={16} />
                           {option.label}
                        </div>
                     ))}
                  </div>
               </FloatingPortal>
               : null
         }
      </>
   )
}

function ActionList(props: {
   sort: { dir: "asc" | "desc", key: string },
   elementID: string | undefined,
   hoverElement: (elementID: string | undefined) => void
   exitElement: () => void
}) {

   let { state: frpState, dispatch: frpDispatch } = useContext(FRPContext);
   let { state: notification, dispatch: notisDispatch } = useContext(FRPNotificationContext);
   let { state: sessionState } = useContext(SessionContext);
   const [visibleCount, setVisibleCount] = useState(20);


   useEffect(() => {
      const actionList = document.querySelector(".FRP_AnnList");
      if (actionList) {
         setVisibleCount(20);
         actionList.scrollTop = 0;
      }
   }, [props.sort.dir, props.elementID]);

   let sortFun = (a: FRPAction, b: FRPAction) => {
      let keys = props.sort.key.split("|")
      let aVal = keys.reduce((acc, key) => acc || a[key], null) || "";
      let bVal = keys.reduce((acc, key) => acc || b[key], null) || "";
      if (aVal === bVal) return 0;
      return aVal.toString().localeCompare(bVal.toString(), undefined, { numeric: true, sensitivity: 'base' });
   }

   const filter = FRPFunc.frpHandleFilterContent(frpState.filter, Object.values(frpState.elements), frpState);

   const filteredActions = frpState.actions
      .filter((a) => a.elementId === props.elementID || props.elementID == undefined)
      .filter(filter)

   const visibleActions = props.sort.dir === "desc"
      ? filteredActions.slice(-visibleCount).sort(sortFun).reverse()
      : filteredActions.slice(0, visibleCount).sort(sortFun);


   const hasMoreActions = filteredActions.length > visibleCount;

   const loadMoreActions = () => {
      const actionList = document.querySelector(".FRP_AnnList");
      if (actionList) {
         const currentScrollTop = actionList.scrollTop;

         setVisibleCount(prev => prev + 20);

         setTimeout(() => {
            actionList.scrollTop = currentScrollTop;
         }, 0);
      } else {
         setVisibleCount(prev => prev + 20);
      }
   };



   return (
      <>
         <div className="FRP_AnnCon" >
            <div className="FRP_AnnList">

               {visibleActions.map((item, index) => (
                  <Fragment key={`${item.id}-${index}-${visibleCount}`}>
                     <SingleAction
                        isUnread={notification[frpState.selectedProjectId || ""]?.[item.elementId || ""] < new Date(item.date) && item.user !== sessionState.userRef}
                        item={item}
                        elementName={props.elementID && frpState.elements[props.elementID]?.name}
                        mouseOver={() => props.hoverElement(item.elementId)}
                        mouseExit={() => props.exitElement()}
                        onClick={() => {
                           if (props.elementID) {
                              notisDispatch(viewedElement(item.elementId || "", new Date(item.date)));
                              return;
                           }
                           FRPFunc.goToElement(frpState, frpDispatch, item.elementId);
                        }}
                     />
                  </Fragment>
               ))}

               {hasMoreActions && (
                  <div style={{
                     display: 'flex',
                     justifyContent: 'center',
                     padding: '10px',
                     marginTop: '10px'
                  }}>
                     <GlassButton onClick={loadMoreActions}>
                        {Localization.getFormattedText("Load more {count} remaining", { count: filteredActions.length - visibleCount })}
                     </GlassButton>
                  </div>
               )}


            </div>
         </div>
      </>
   );
}

function SingleAction(props: { isUnread: boolean, item: FRPAction, elementName: string | undefined, mouseOver?: (e: React.MouseEvent<HTMLDivElement>) => void, mouseExit?: (e: React.MouseEvent<HTMLDivElement>) => void, onClick?: () => void }) {

   return (
      <div className="FRP_AnnItem"
         onMouseMove={props.mouseOver}
         onMouseLeave={props.mouseExit}
         onClick={props.onClick} >
         {props.isUnread ? <span className="FRP_AnnItemTitleUnread" /> : null}
         <div className="quick">
            <div className="profile">
               {props.item.user?.[0].toUpperCase()}
            </div>
         </div>
         <div className="content">
            <div className="head">
               <span className="FRP_AnnItemUser">{props.item.user}</span>
               <span className="FRP_AnnItemElement">{props.elementName}</span>
               <span className="FRP_AnnItemDate">{formatDate(new Date(props.item.date))}</span>
            </div>
            <div className="description">
               {props.item.message}
            </div>
         </div>
      </div>
   )
}

function MessageList(props: {
   elementID: string | undefined,
   sort: { dir: "asc" | "desc", key: string },
   hoverElement: (elementID: string | undefined) => void
   exitElement: () => void
}) {

   let { state: frpState, dispatch: frpDispatch } = useContext(FRPContext);
   let { state: notification, dispatch: notisDispatch } = useContext(FRPNotificationContext);
   let { state: sessionState } = useContext(SessionContext);
   const [visibleCount, setVisibleCount] = useState(20);

   useEffect(() => {
      const actionList = document.querySelector(".FRP_AnnList");
      if (actionList) {
         setVisibleCount(20);
         actionList.scrollTop = 0;
      }
   }, [props.sort.dir, props.elementID]);

   let sortFun = (a: FRPMessage, b: FRPMessage) => {
      let keys = props.sort.key.split("|")
      let aVal = keys.reduce((acc, key) => acc || a[key], null) || "";
      let bVal = keys.reduce((acc, key) => acc || b[key], null) || "";
      if (aVal === bVal) return 0;
      return aVal.toString().localeCompare(bVal.toString(), undefined, { numeric: true, sensitivity: 'base' });
   }

   const filter = FRPFunc.frpHandleFilterContent(frpState.filter, Object.values(frpState.elements), frpState);

   const filteredMessages = frpState.messages
      .filter((a) => a.elementId === props.elementID || props.elementID == undefined)
      .filter(filter);


   const visibleMessages = props.sort.dir === "desc"
      ? filteredMessages.slice(-visibleCount).sort(sortFun).reverse()
      : filteredMessages.slice(0, visibleCount).sort(sortFun);

   const hasMoreMessages = filteredMessages.length > visibleCount;

   const loadMoreMessages = () => {
      const actionList = document.querySelector(".FRP_AnnList");
      if (actionList) {
         const currentScrollTop = actionList.scrollTop;

         setVisibleCount(prev => prev + 20);

         setTimeout(() => {
            actionList.scrollTop = currentScrollTop;
         }, 0);
      } else {
         setVisibleCount(prev => prev + 20);
      }
   };

   return (
      <div className="FRP_AnnCon" >
         <div className="FRP_AnnList" key={props.elementID + "msg"} onLoad={(e) => {
            // Scroll to the bottom when the component loads
            e.currentTarget.scrollTop = e.currentTarget.scrollHeight;
         }}>
            {visibleMessages.map((item, index) => (
               <Fragment key={index}>
                  <SingleMessage
                     onClick={() => {
                        if (props.elementID) {
                           notisDispatch(viewedElement(item.elementId || "", new Date(item.edited || item.created)));
                           return;
                        }
                        FRPFunc.goToElement(frpState, frpDispatch, item.elementId);
                     }}
                     isUnread={notification[frpState.selectedProjectId || ""]?.[item.elementId || ""] < new Date(item.edited || item.created) && item.user !== sessionState.userRef}
                     mouseOver={() => props.hoverElement(item.elementId)}
                     mouseExit={() => props.exitElement()}
                     item={item}
                     change={() => { return null; }}
                     user={""}
                     elementName={""}
                     onDelete={() => frpDispatch(deleteElements(item.id))}
                  />
               </Fragment>
            ))}

            {hasMoreMessages && (
               <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  padding: '10px',
                  marginTop: '10px'
               }}>
                  <GlassButton onClick={loadMoreMessages}>
                     {Localization.getFormattedText("Load more {count} remaining", { count: filteredMessages.length - visibleCount })}
                  </GlassButton>
               </div>
            )}
         </div>
         <div className="FRP_AnnInput">
            <textarea className="FRP_AnnInputArea" placeholder={Localization.getText("Skriv besked...")} onKeyDown={(e: React.KeyboardEvent<HTMLTextAreaElement>) => {
               if (e.key === "Enter" && e.ctrlKey) {
                  e.preventDefault();
                  const msg: FRPMessage = {
                     id: MapitUtils.getNewUuid(),
                     elementId: props.elementID || undefined,
                     type: FRPElementType.Message,
                     content: (e.currentTarget.value || "New message content.").trim(),
                     created: new Date(),
                     user: sessionState.userRef,
                  };
                  e.currentTarget.value = ""; // Clear the input after adding
                  frpDispatch(addElement(msg.id, msg));
               }
            }} />
            <div className="FRP_options">
               <button className="i_btn" title="Ctrl + Enter" onClick={(e) => {
                  e.preventDefault();
                  let textarea = e.currentTarget.parentElement?.parentElement?.querySelector(".FRP_AnnInputArea");
                  const msg: FRPMessage = {
                     id: MapitUtils.getNewUuid(),
                     elementId: props.elementID || undefined,
                     type: FRPElementType.Message,
                     content: (textarea?.["value"] || "New message content.").trim(),
                     created: new Date(),
                     user: sessionState.userRef,
                  };
                  if (textarea) {
                     textarea["value"] = ""; // Clear the input after adding
                  }
                  frpDispatch(addElement(msg.id, msg));
               }}>
                  <SaveAdd size={16} /> Send
               </button>
            </div>
         </div>
      </div>
   );
}

function SingleMessage(props: { isUnread: boolean, user: string, item: FRPMessage, elementName: string, change?: (type, data) => void, mouseOver?: (e: React.MouseEvent<HTMLDivElement>) => void, mouseExit?: (e: React.MouseEvent<HTMLDivElement>) => void, onClick?: () => void, onDelete?: () => void }) {



   return (
      <div className="FRP_AnnItem"
         onMouseMove={props.mouseOver}
         onMouseLeave={props.mouseExit}
         onClick={props.onClick} >
         {props.isUnread ? <span className="FRP_AnnItemTitleUnread" /> : null}
         <div className="quick">
            <div className="profile">
               {props.item.user?.[0].toUpperCase()}
            </div>
         </div>
         <div className="content">
            <div className="head">
               <span className="FRP_AnnItemUser">{props.item.user}</span>
               <span className="FRP_AnnItemElement">{props.elementName}</span>
               <span className="FRP_AnnItemDate">{formatDate(new Date(props.item.created))}</span>
               <button
                  className="FRP_MessageDeleteBtn"
                  title={Localization.getText("Delete")}
                  onClick={(e) => {
                     e.stopPropagation();
                     props.onDelete?.();
                  }}
               >
                  <Trash size={14} />
               </button>
               {/* <span className="FRP_reactions">{props.item.reactions}</span> */}
            </div>
            <div className="description">
               <span className="FRP_AnnItemElementID">{props.item.elementId || Localization.getText("Project")}<br /></span>
               {props.item.content}
            </div>
         </div>
      </div>
   )
}

function FRPElementList(props: {
   sort: { dir: "asc" | "desc", key: string },
   elementID: string | undefined,
   hoverElement: (elementID: string | undefined) => void
   exitElement: () => void
}) {
   const { state: sessionState } = useContext(SessionContext);
   const { state: frpState, dispatch: frpDispatch } = useContext(FRPContext);
   const { state: notification } = useContext(FRPNotificationContext);

   let sortFun = (a: FRPElement, b: FRPElement) => {
      let keys = props.sort.key.split("|")
      let aVal = keys.reduce((acc, key) => acc || a[key], null) || "";
      let bVal = keys.reduce((acc, key) => acc || b[key], null) || "";
      if (aVal === bVal) return 0;
      return aVal.toString().localeCompare(bVal.toString(), undefined, { numeric: true, sensitivity: 'base' }) * (props.sort.dir === "asc" ? 1 : -1);
   }

   const filter = FRPFunc.frpHandleFilter(frpState.filter, frpState);



   return (
      <div className="FRP_AnnCon" >
         <div className="FRP_AnnList">
            {Object.values(frpState.elements)
               .filter((el) => {
                  if (frpState.selectedElementIds && frpState.selectedElementIds.length > 1) {
                     return frpState.selectedElementIds.includes(el.id);
                  }
                  return FRPFunc.filterByChildren(el, props.elementID, frpState.childrenLookupTable);
               })
               .filter(filter)
               .sort(sortFun)
               .map((element) => (
                  <Fragment key={element.id}>
                     <SingleElement
                        isUnread={notification[frpState.selectedProjectId || ""]?.[element.id] < new Date(element.lastChangedTimestamp) && element.lastChangedUser !== sessionState.userRef}
                        element={element}
                        onClick={() => {
                           frpDispatch(setView('element'));
                           frpDispatch(setSelectedElementIds(element.id ? [element.id] : []));
                           frpDispatch(setShownElementsIds(element.refElementId ? frpState.childrenLookupTable[element.refElementId] ?? [element.refElementId] : FRPFunc.determineAllRootElements(frpState)));
                        }}
                        mouseOver={() => props.hoverElement(element.id)}
                        mouseExit={() => props.exitElement()}
                     />
                  </Fragment>
               ))}
         </div>
      </div>
   );
}

function SingleElement(props: { isUnread: boolean, element: FRPElement, onClick?: () => void, mouseOver?: (e: React.MouseEvent<HTMLDivElement>) => void, mouseExit?: (e: React.MouseEvent<HTMLDivElement>) => void }) {
   const isInvalid = FRPFunc.isElement(props.element) === false;

   if (isInvalid) {

      return (
         <div className="FRP_AnnItem FRP_invalidElement">

            <div className="content">
               <div className="head">
                  <span className="FRP_AnnItemUser">❗Invalid Element</span>
               </div>
               <div className="description">This element is invalid or corrupted.</div>
            </div>
         </div>
      );
   }

   return (
      <div className="FRP_AnnItem"
         onMouseMove={props.mouseOver}
         onMouseLeave={props.mouseExit}
         onClick={props.onClick} >
         {props.isUnread ? <span className="FRP_AnnItemTitleUnread" /> : null}
         <div className="content">
            <div className="head">
               <span className="FRP_AnnItemUser">{props.element.id}</span>
            </div>
         </div>
      </div>
   );
}